import { CheckCircle2, Circle, CircleDot } from "lucide-react";

// Buyer/vendor/admin canonical stage progression.
const STAGES = [
  { key: "paid", label: "Paid" },
  { key: "awaiting_vendor", label: "Awaiting Vendor" },
  { key: "processing", label: "Processing" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
];

// Map an order to the index of the currently active stage.
function activeIndex(order) {
  const s = (order?.status || "").toLowerCase();
  const ps = (order?.payment_status || "").toLowerCase();
  if (s === "cancelled" || s === "payment_failed") return -1;
  if (s === "delivered") return 4;
  if (s === "shipped") return 3;
  if (s === "processing" || s === "packed") return 2;
  if (s === "awaiting_vendor") return 1;
  // paid / cod pending / awaiting payment — first pill lights up only if paid or cod
  if (ps === "paid" || ps === "cod_pending") return 0;
  return -1;
}

export function PaymentStatusBadge({ order }) {
  const ps = (order?.payment_status || "").toLowerCase();
  const map = {
    paid: { text: "Paid", cls: "bg-green-50 text-green-700 border-green-200" },
    cod_pending: { text: "COD on delivery", cls: "bg-amber-50 text-amber-700 border-amber-200" },
    awaiting_payment: { text: "Awaiting payment", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    awaiting_confirmation: { text: "Awaiting bank transfer", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    failed: { text: "Payment failed", cls: "bg-red-50 text-red-700 border-red-200" },
    pending: { text: "Pending", cls: "bg-gray-50 text-gray-600 border-gray-200" },
  };
  const m = map[ps] || map.pending;
  return (
    <span data-testid="payment-status" className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${m.cls}`}>{m.text}</span>
  );
}

export default function OrderStagePill({ order }) {
  const idx = activeIndex(order);
  const cancelled = ["cancelled", "payment_failed"].includes((order?.status || "").toLowerCase());
  return (
    <div data-testid="order-stage-pill" className="mt-3">
      <div className="flex flex-wrap items-center gap-2 gap-y-1">
        <PaymentStatusBadge order={order} />
        {cancelled && (
          <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">
            {order.status === "payment_failed" ? "Payment failed" : "Cancelled"}
          </span>
        )}
      </div>
      {!cancelled && (
        <ol className="mt-2 flex flex-wrap items-center gap-x-1 gap-y-2 text-[11px]">
          {STAGES.map((s, i) => {
            const done = i < idx;
            const current = i === idx;
            const Icon = done ? CheckCircle2 : current ? CircleDot : Circle;
            const cls = done ? "text-green-700" : current ? "text-[#F48E2E] font-semibold" : "text-gray-400";
            return (
              <li key={s.key} className="flex items-center gap-1">
                <Icon className={`h-3.5 w-3.5 ${cls}`} />
                <span className={cls}>{s.label}</span>
                {i < STAGES.length - 1 && <span className="text-gray-300 mx-1">→</span>}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
